import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  incident_type: 'near_miss',
  severity: 'minor',
  client_id: '',
  title: '',
  occurred_at: new Date().toISOString().slice(0, 16),
  location: '',
  description: '',
  immediate_response: '',
  cause_analysis: '',
  prevention_measures: '',
  injury_detail: '',
  status: 'draft'
};

export default function IncidentFormPage() {
  const navigate = useNavigate();
  const { incidentId } = useParams();
  const isEditing = !!incidentId;

  const [form, setForm] = useState(INITIAL_FORM);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
    if (isEditing) fetchIncident();
  }, [incidentId]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchIncident = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/incidents/${incidentId}`);
      const d = res.data;
      setForm({
        incident_type: d.incident_type,
        severity: d.severity,
        client_id: d.client_id || '',
        title: d.title,
        occurred_at: d.occurred_at?.slice(0, 16) || '',
        location: d.location || '',
        description: d.description || '',
        immediate_response: d.immediate_response || '',
        cause_analysis: d.cause_analysis || '',
        prevention_measures: d.prevention_measures || '',
        injury_detail: d.injury_detail || '',
        status: d.status
      });
    } catch (e) {
      console.error(e);
      navigate('/incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_id) payload.client_id = null;

      if (isEditing) {
        await axios.put(`${API}/incidents/${incidentId}`, payload);
      } else {
        await axios.post(`${API}/incidents`, payload);
      }
      navigate('/incidents');
    } catch (error) {
      console.error('保存に失敗', error);
      alert('保存に失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="incident-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/incidents')} data-testid="back-button">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {isEditing ? 'インシデント編集' : '新規インシデント報告'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditing ? '報告内容を更新します' : '事故報告またはヒヤリハットを登録'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="card-surface">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert size={20} className="text-primary" />
              基本情報
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>種別 *</Label>
                <Select value={form.incident_type} onValueChange={(v) => handleChange('incident_type', v)}>
                  <SelectTrigger data-testid="incident-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accident">事故報告</SelectItem>
                    <SelectItem value="near_miss">ヒヤリハット</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>重大度 *</Label>
                <Select value={form.severity} onValueChange={(v) => handleChange('severity', v)}>
                  <SelectTrigger data-testid="severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">重大</SelectItem>
                    <SelectItem value="moderate">中程度</SelectItem>
                    <SelectItem value="minor">軽微</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>タイトル *</Label>
              <Input
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="インシデントの概要"
                required
                data-testid="title-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>対象利用者</Label>
                <Select value={form.client_id || 'none'} onValueChange={(v) => handleChange('client_id', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ステータス</Label>
                <Select value={form.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">下書き</SelectItem>
                    <SelectItem value="reported">報告済</SelectItem>
                    <SelectItem value="in_progress">対応中</SelectItem>
                    <SelectItem value="completed">完了</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>発生日時 *</Label>
                <Input
                  type="datetime-local"
                  value={form.occurred_at}
                  onChange={(e) => handleChange('occurred_at', e.target.value)}
                  required
                  data-testid="occurred-at-input"
                />
              </div>
              <div className="space-y-2">
                <Label>発生場所</Label>
                <Input
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="例: 食堂、トイレ前廊下"
                  data-testid="location-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface mt-4">
          <CardHeader>
            <CardTitle>詳細情報</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>発生状況 *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="何が起きたか、詳しく記載してください"
                rows={4}
                required
                data-testid="description-input"
              />
            </div>

            {form.incident_type === 'accident' && (
              <div className="space-y-2">
                <Label>受傷内容</Label>
                <Textarea
                  value={form.injury_detail}
                  onChange={(e) => handleChange('injury_detail', e.target.value)}
                  placeholder="怪我の種類・程度"
                  rows={2}
                  data-testid="injury-detail-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>直後の対応</Label>
              <Textarea
                value={form.immediate_response}
                onChange={(e) => handleChange('immediate_response', e.target.value)}
                placeholder="発生直後にどのような対応をしたか"
                rows={3}
                data-testid="immediate-response-input"
              />
            </div>

            <div className="space-y-2">
              <Label>原因分析</Label>
              <Textarea
                value={form.cause_analysis}
                onChange={(e) => handleChange('cause_analysis', e.target.value)}
                placeholder="なぜ発生したか（環境要因、人的要因など）"
                rows={3}
                data-testid="cause-analysis-input"
              />
            </div>

            <div className="space-y-2">
              <Label>再発防止策</Label>
              <Textarea
                value={form.prevention_measures}
                onChange={(e) => handleChange('prevention_measures', e.target.value)}
                placeholder="今後どのような対策を講じるか"
                rows={3}
                data-testid="prevention-measures-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/incidents')} data-testid="cancel-button">
            キャンセル
          </Button>
          <Button type="submit" className="btn-active" disabled={saving} data-testid="submit-button">
            <Save size={16} className="mr-2" />
            {saving ? '保存中...' : (isEditing ? '更新' : '登録')}
          </Button>
        </div>
      </form>
    </div>
  );
}
